'use client';

import { useCallback, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { CheckCircleIcon, DeviceMobileIcon, EnvelopeSimpleIcon, HashIcon, IdentificationCardIcon, UserCircleIcon } from '@phosphor-icons/react';

import Image from '@/components/ui/Image';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { BillingView, PlanSummary } from '@/components/BillingView';
import { useProfile } from '@/hooks/queries/useProfile';
import { BillingAddressSection } from '@/components/profile/BillingAddressSection';
import { ChatMemoriesSection } from '@/components/profile/ChatMemoriesSection';
import { McpConnectorSection } from '@/components/profile/McpConnectorSection';
import { NotificationPreferencesSection } from '@/components/notifications/NotificationPreferencesSection';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function ProfileDetail({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: typeof UserCircleIcon;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex min-w-0 gap-3 border-t border-border-strong py-3 first:border-t-0 sm:border-t-0 sm:py-0">
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-surface-sunken text-brand-primary">
        <Icon className="size-4" weight="regular" />
      </div>
      <div className="min-w-0">
        <dt className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">{label}</dt>
        <dd
          className={`mt-0.5 truncate text-sm font-medium text-text-primary ${mono ? 'font-mono' : ''}`}
          title={value}
        >
          {value}
        </dd>
      </div>
    </div>
  );
}

export function ProfileView() {
  const { user } = useUser();
  // Assinar pre-flight (VER-389): BillingView hands the blocked plan up, the
  // address section opens + scrolls into view, and saving resumes the checkout.
  const [blockedPlan, setBlockedPlan] = useState<{ code: string; name: string } | null>(null);
  const [resumePlanCode, setResumePlanCode] = useState<string | null>(null);

  const handleBillingAddressRequired = useCallback((plan: { code: string; name: string }) => {
    setBlockedPlan(plan);
    document.getElementById('dados-de-cobranca')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleAddressSaved = useCallback(() => {
    if (blockedPlan) {
      setResumePlanCode(blockedPlan.code);
      setBlockedPlan(null);
    }
  }, [blockedPlan]);

  const handleResumeConsumed = useCallback(() => setResumePlanCode(null), []);
  const { data: profile, error: profileError, isLoading: profileLoading } = useProfile();
  const displayName =
    user?.fullName ??
    user?.primaryEmailAddress?.emailAddress ??
    user?.username ??
    'Usuário';
  const email = user?.primaryEmailAddress?.emailAddress ?? 'Sem e-mail principal';
  const avatarLabel = initials(displayName) || displayName.charAt(0).toUpperCase();
  // A failed request must never read as "Não cadastrado": this is the page a user
  // opens specifically to check whether their CPF is registered, and the two states
  // are indistinguishable without the error branch.
  const cpfDisplay = profileLoading
    ? 'Carregando'
    : profileError
      ? 'Erro ao carregar'
      : profile?.cpfMasked ?? 'Não cadastrado';
  const whatsAppDisplay = profileLoading
    ? 'Carregando'
    : profileError
      ? 'Erro ao carregar'
      : profile?.whatsAppMasked ?? 'Não cadastrado';

  return (
    <div data-tokens="v2" className="flex flex-col gap-6">
      <PageHeader
        title="Perfil"
        description="Dados da conta, segurança e assinatura."
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(240px,0.7fr)_minmax(0,1.3fr)] lg:gap-0">
        <div className="flex min-w-0 flex-col gap-6 lg:border-r lg:border-border-strong lg:pr-8">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-brand-primary text-on-brand">
              {user?.imageUrl ? (
                <Image
                  src={user.imageUrl}
                  alt={displayName}
                  width={56}
                  height={56}
                  className="size-14 object-cover"
                />
              ) : (
                <span className="text-lg font-bold">{avatarLabel}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-lg font-semibold text-text-primary">{displayName}</p>
                <Badge variant="success" className="ml-auto shrink-0 py-1 pr-2.5 pl-1.5">
                  <CheckCircleIcon className="size-3.5" weight="fill" />
                  Conta ativa
                </Badge>
              </div>
              <p className="mt-1 truncate text-sm text-text-secondary">{email}</p>
            </div>
          </div>

          <PlanSummary />
        </div>

        <div className="lg:pl-8">
          <div className="mb-4">
            <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
              Informações da conta
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              Dados usados para autenticação e Open Finance.
            </p>
          </div>
          <dl className="grid gap-x-6 sm:grid-cols-2 sm:gap-y-5">
            <ProfileDetail icon={UserCircleIcon} label="Nome" value={displayName} />
            <ProfileDetail icon={EnvelopeSimpleIcon} label="E-mail" value={email} />
            <ProfileDetail icon={IdentificationCardIcon} label="CPF" value={cpfDisplay} />
            <ProfileDetail icon={DeviceMobileIcon} label="WhatsApp" value={whatsAppDisplay} />
            <ProfileDetail icon={HashIcon} label="ID da conta" value={user?.id ?? 'Carregando'} mono />
          </dl>

        </div>
      </div>

      <NotificationPreferencesSection />

      <ChatMemoriesSection />

      <McpConnectorSection />

      <BillingAddressSection
        blockedPlanName={blockedPlan?.name ?? null}
        onSaved={handleAddressSaved}
      />

      <BillingView
        embedded
        showCurrentPlan={false}
        onBillingAddressRequired={handleBillingAddressRequired}
        resumePlanCode={resumePlanCode}
        onResumeConsumed={handleResumeConsumed}
      />
    </div>
  );
}
